import React, { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import spellCatalog from '../../../data/spells.json';
import {
  MAGIC_INITIATE_ABILITIES, MAGIC_INITIATE_CANTRIP_COUNT, MAGIC_INITIATE_LISTS, getMagicInitiateChoice, canChangeMagicInitiate,
} from '../../../shared/rules/magicInitiate.mjs';

const spellIdFromName = (name) => String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const spellsFor = (list, level) => spellCatalog
  .filter((spell) => Number(spell.level) === level && (spell.classes || []).map((entry) => String(entry).toLowerCase()).includes(list))
  .map((spell) => ({ id: spellIdFromName(spell.name), name: spell.name }))
  .sort((a, b) => a.name.localeCompare(b.name));
const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Magic Initiate (feat): choose two cantrips and a level 1 spell from the feat's list, and the spellcasting ability.
 * The ability is fixed once saved; after gaining a level, one spell can be replaced.
 */
export default function MagicInitiateChooser({ form, characterId, list, onCharacterChange }) {
  const label = MAGIC_INITIATE_LISTS[list];
  const [saved, setSaved] = useState(null);
  const current = saved || getMagicInitiateChoice(form, list);
  const [ability, setAbility] = useState(current?.ability || '');
  const [cantrips, setCantrips] = useState(() => (current?.cantrips?.length ? [...current.cantrips] : Array(MAGIC_INITIATE_CANTRIP_COUNT).fill('')));
  const [spell, setSpell] = useState(current?.spell || '');
  const [saving, setSaving] = useState(false), [error, setError] = useState('');
  const locked = Boolean(current) && !canChangeMagicInitiate(saved ? { ...form, featState: { ...form.featState, magicInitiate: { ...form.featState?.magicInitiate, [list]: saved } } } : form, list);
  const ready = ability && spell && cantrips.every(Boolean) && new Set(cantrips).size === cantrips.length;
  const choose = async () => {
    if (saving) return;
    setSaving(true); setError('');
    try {
      const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/feats/magic-initiate/${list}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...(current ? {} : { ability }), cantrips, spell }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Could not save Magic Initiate spells.');
      setSaved(result.magicInitiate);
      onCharacterChange?.(previous => ({ ...previous, featState: { ...previous.featState, magicInitiate: { ...previous.featState?.magicInitiate, [list]: result.magicInitiate } } }));
    } catch (failure) { setError(failure.message); }
    finally { setSaving(false); }
  };
  return <div className="fighting-style-chooser magic-initiate-chooser">
    <p className="feature-sheet-hint">{!current
      ? `Choose two ${label} cantrips, a level 1 ${label} spell and your spellcasting ability. The level 1 spell is always prepared: cast it once per Long Rest without a slot, or with any spell slot.`
      : locked ? 'You can replace one of these spells the next time you gain a level.' : 'You gained a level: you can replace one of these spells.'}</p>
    {error && <Alert variant="danger">{error}</Alert>}
    {saved && <Alert variant="success">Magic Initiate spells saved.</Alert>}
    <Form.Group controlId={`magic-initiate-${list}-ability`}>
      <Form.Label>Spellcasting ability</Form.Label>
      <Form.Select value={ability} disabled={saving || Boolean(current)} onChange={event => setAbility(event.target.value)}>
        <option value="">Choose an ability</option>
        {MAGIC_INITIATE_ABILITIES.map(key => <option key={key} value={key}>{capitalize(key)}</option>)}
      </Form.Select>
    </Form.Group>
    <div className="fighting-style-cantrips">
      {cantrips.map((value, index) => (
        <Form.Group key={index} controlId={`magic-initiate-${list}-cantrip-${index}`}>
          <Form.Label>{`${label} cantrip ${index + 1}`}</Form.Label>
          <Form.Select value={value} disabled={saving || locked} onChange={event => setCantrips(previous => previous.map((entry, i) => (i === index ? event.target.value : entry)))}>
            <option value="">Choose a cantrip</option>
            {spellsFor(list, 0).map(option => <option key={option.id} value={option.id} disabled={cantrips.includes(option.id) && value !== option.id}>{option.name}</option>)}
          </Form.Select>
        </Form.Group>
      ))}
    </div>
    <Form.Group controlId={`magic-initiate-${list}-spell`}>
      <Form.Label>{`Level 1 ${label} spell`}</Form.Label>
      <Form.Select value={spell} disabled={saving || locked} onChange={event => setSpell(event.target.value)}>
        <option value="">Choose a spell</option>
        {spellsFor(list, 1).map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
      </Form.Select>
    </Form.Group>
    <div className="fighting-style-actions"><Button size="sm" onClick={choose} disabled={saving || locked || !ready}>{saving ? 'Saving…' : 'Save Magic Initiate spells'}</Button></div>
  </div>;
}
